import React from "react";
import { pascalCaseToTitleCase } from "../../../../../../utils/casing";
import { Link, Tooltip } from "@merge-api/merge-javascript-shared";

interface Props {
  /** Raw model name of the related object; also that object row's DOM id, used to jump to it. */
  relatedModelName: string;
  /** Field on the related object that co-toggles, shown when the related object is enabled. */
  relatedFieldName?: string;
  isRelatedObjectEnabled: boolean;
}

/**
 * Inline relation sentence for a field that references another top-level object. Rendered as a
 * lead-in before the field's own description (see ScopeFieldRow) so the relationship reads as one
 * sentence preceding another. The wording escalates to a call-to-action when the related object is
 * disabled. The object name links to that object's row (scrolls to it) so the user can toggle it
 * there. Guidance only, no hard lock: the field itself stays toggleable.
 */
const ScopeRelationNote = ({
  relatedModelName,
  relatedFieldName,
  isRelatedObjectEnabled,
}: Props) => {
  // event handlers
  const onJumpToObject = () => {
    document
      .getElementById(relatedModelName)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // render
  // Force as="a" so Link renders an <a>, not a <button> with native padding (Link defaults to
  // <button> when given onClick but no href). Passing className intentionally clobbers Link's own
  // blue styling — we want gray-50 (#565551, matches the description text) underlined instead.
  // Tooltip is scoped to just the link text — it wraps the Link only, not the surrounding sentence.
  const objectName = pascalCaseToTitleCase(relatedModelName);
  const objectLink = (
    <Tooltip title={`Go to ${objectName}`}>
      <Link
        variant="sm-mono"
        as="a"
        onClick={onJumpToObject}
        className="cursor-pointer text-gray-50 underline"
      >
        {objectName}
      </Link>
    </Tooltip>
  );

  if (!isRelatedObjectEnabled) {
    return <>Enable the {objectLink} object to enable toggling this field.</>;
  }
  if (relatedFieldName) {
    return (
      <>
        Toggling this field also toggles the {relatedFieldName} field on the {objectLink} object.
      </>
    );
  }
  return <>References the {objectLink} object.</>;
};

export default ScopeRelationNote;
